import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('pointerdown', this.#handleMouseDown);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  /**
   * @param {HTMLElement | undefined} slide
   */
  #revealSlide(slide) {
    if (!slide?.hasAttribute('hidden')) return;

    slide.setAttribute('reveal', '');
    slide.setAttribute('aria-hidden', 'false');
  }

  /**
   * Resolves a slide from an absolute index, slide id, or filtered index input.
   * Thumbnail controls pass absolute indices; arrows and dots use filtered indices.
   * @param {number|string|{id: string}} input
   * @returns {HTMLElement | undefined}
   */
  #resolveTargetSlide(input) {
    const allSlides = this.refs.slides;
    if (!allSlides?.length) return;

    if (typeof input === 'object' && input !== null && 'id' in input) {
      const slide = allSlides.find((entry) => entry.getAttribute('slide-id') == input.id);
      this.#revealSlide(slide);
      return slide;
    }

    const numericInput = typeof input === 'number' ? input : parseInt(String(input), 10);
    if (isNaN(numericInput)) return;

    const filteredSlides = this.slides;
    const slideByAbsoluteIndex = allSlides[numericInput];

    if (slideByAbsoluteIndex) {
      this.#revealSlide(slideByAbsoluteIndex);
      return slideByAbsoluteIndex;
    }

    return filteredSlides?.[numericInput];
  }

  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];
    const { slides } = this;
    if (!slides?.length) return;

    // Keep out-of-range numeric indexes for infinite loop direction, then resolve a real slide.
    let requestedIndex;
    let resolveInput = input;
    const numericInput =
      typeof input === 'number' ? input : typeof input === 'string' ? parseInt(input, 10) : NaN;

    if (!Number.isNaN(numericInput)) {
      requestedIndex = numericInput;
      if (this.infinite) {
        if (numericInput > slides.length - 1) resolveInput = 0;
        else if (numericInput < 0) resolveInput = slides.length - 1;
      } else {
        requestedIndex = clamp(numericInput, 0, slides.length - 1);
        resolveInput = requestedIndex;
      }
    }

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    const targetSlide = this.#resolveTargetSlide(resolveInput);
    const { current } = this;

    // Guard checks: no slides, invalid target, or selecting the same slide
    if (!targetSlide) return;

    if (requestedIndex === undefined) {
      requestedIndex = slides.indexOf(targetSlide);
      if (requestedIndex < 0) return;
    }

    if (currentSlide === targetSlide) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (wrap for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isWrapAround = requestedIndex < 0 || requestedIndex > lastIndex;
    const isAdjacentSlide = Math.abs(index - current) <= 1 && !isWrapAround;
    const { visibleSlides } = this;
    let instant = prefersReducedMotion() || !animate;

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;
    const absoluteThumbnailIndex = this.refs.slides?.indexOf(slide) ?? index;

    // Infinite wrap: reorder → short scroll → restore. Sequential + try/finally so it
    // never gets stuck disabled or falls into a long reverse scroll.
    if (!instant && isWrapAround && currentSlide) {
      this.#disabled = true;
      try {
        await this.#awaitScrollEnd();

        const placeholder = document.createElement('slideshow-slide');
        slide.before(placeholder);

        if (requestedIndex < current) {
          currentSlide.before(slide);
        } else {
          currentSlide.after(slide);
        }

        if (current === 0) {
          this.#scroll.to(currentSlide, { instant: true });
          await this.#awaitScrollEnd();
        }

        slide.setAttribute('aria-hidden', 'false');
        this.#scroll.to(slide, { instant: false });
        this.current = index;
        if (!this.#isThumbnailSelectEvent(event)) {
          this.#centerSelectedThumbnail(absoluteThumbnailIndex, 'smooth');
        }

        this.dispatchEvent(
          new SlideshowSelectEvent({
            index,
            previousIndex,
            userInitiated: event != null,
            trigger: 'select',
            slide,
            id: slide.getAttribute('slide-id'),
          })
        );

        await this.#awaitScrollEnd();

        placeholder.replaceWith(slide);
        this.#scroll.to(slide, { instant: true });
        this.current = index;

        requestAnimationFrame(() => {
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            this.removeAttribute('refreshing-timeline');
          });
        });
      } catch {
        // If wrap animation fails, hard-jump so autoplay never stalls on the last slide
        this.#scroll.to(slide, { instant: true });
        this.current = index;
      } finally {
        this.#disabled = false;
      }
      return;
    }

    // Non-wrap large jump (e.g. dots): optional reorder when exactly one slide is visible
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1 && currentSlide) {
      this.#disabled = true;
      try {
        await this.#awaitScrollEnd();

        const placeholder = document.createElement('slideshow-slide');
        slide.before(placeholder);

        if (requestedIndex < current) {
          currentSlide.before(slide);
        } else {
          currentSlide.after(slide);
        }

        if (current === 0) this.#scroll.to(currentSlide, { instant: true });

        slide.setAttribute('aria-hidden', 'false');
        this.#scroll.to(slide, { instant: false });
        this.current = index;

        await this.#awaitScrollEnd();

        placeholder.replaceWith(slide);
        this.#scroll.to(slide, { instant: true });
      } catch {
        this.#scroll.to(slide, { instant: true });
        this.current = index;
      } finally {
        this.#disabled = false;
      }

      if (!this.#isThumbnailSelectEvent(event)) {
        this.#centerSelectedThumbnail(absoluteThumbnailIndex, 'smooth');
      }
      this.dispatchEvent(
        new SlideshowSelectEvent({
          index,
          previousIndex,
          userInitiated: event != null,
          trigger: 'select',
          slide,
          id: slide.getAttribute('slide-id'),
        })
      );
      return;
    }

    // Wrap fallback: jump instantly instead of a slow full-carousel reverse scroll
    if (isWrapAround) {
      instant = true;
    }

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    // Use logical index — DOM may be reordered; indexOf after mutation is unreliable
    this.current = index;

    if (!this.#isThumbnailSelectEvent(event)) {
      this.#centerSelectedThumbnail(absoluteThumbnailIndex, instant ? 'instant' : 'smooth');
    }

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Thumbnail taps already point at the desired thumb — recentering the strip
   * on mobile looks like the thumbnails list is swiping.
   * @param {Event | undefined} event
   */
  #isThumbnailSelectEvent(event) {
    const target = event?.target;
    return target instanceof Element && Boolean(target.closest('.slideshow-controls__thumbnail'));
  }

  /**
   * Wait for the current scroller animation to end, with a timeout so wrap
   * never hangs if the browser skips scrollend.
   * @param {number} [timeout=1200]
   * @returns {Promise<void>}
   */
  #awaitScrollEnd(timeout = 1200) {
    if (!this.#scroll) return Promise.resolve();
    return Promise.race([
      this.#scroll.finished,
      new Promise((resolve) => {
        setTimeout(resolve, timeout);
      }),
    ]);
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    const slides = this.slides;
    if (this.infinite && slides?.length && this.current >= slides.length - 1) {
      // Force wrap path with an out-of-range index (never a long reverse scroll)
      this.select(slides.length, event, options);
      return;
    }
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    const slides = this.slides;
    if (this.infinite && slides?.length && this.current <= 0) {
      this.select(-1, event, options);
      return;
    }
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden || this.#disabled) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, previous, next } = this.refs;
    const slides = this.slides;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    const activeSlide = slides?.[value];
    const absoluteThumbnailIndex =
      activeSlide != null ? this.refs.slides?.indexOf(activeSlide) ?? value : value;

    /**
     * `refs.thumbnails` is every thumbnail in the component flattened into one
     * list, and a component can own more than one strip: the product gallery
     * renders a vertical strip for desktop and a horizontal one for mobile. The
     * second strip's buttons therefore continue the list past the slide count,
     * so comparing their position against a slide index never matches and that
     * strip loads with nothing selected. Counting within each strip fixes it and
     * is identical for a slideshow that only has one.
     * @type {Map<Element, Element[]>}
     */
    const thumbnailsByStrip = new Map();
    for (const thumbnail of thumbnails ?? []) {
      const strip = thumbnail.closest('slideshow-controls') ?? this;
      const stripThumbnails = thumbnailsByStrip.get(strip);
      if (stripThumbnails) stripThumbnails.push(thumbnail);
      else thumbnailsByStrip.set(strip, [thumbnail]);
    }

    for (const stripThumbnails of thumbnailsByStrip.values()) {
      stripThumbnails.forEach((el, i) => el.setAttribute('aria-selected', `${i === absoluteThumbnailIndex}`));
    }

    dots?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    // pointerdown covers mouse + touch so infinite wrap on the last/first slide works
    scroller.addEventListener('pointerdown', this.#handleMouseDown);
    // Firefox: preventDefault on pointerdown does not stop native <a>/<img> drag.
    scroller.addEventListener('dragstart', preventDefault);
    scroller.querySelectorAll('img, a, video').forEach((node) => {
      node.setAttribute('draggable', 'false');
    });

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'pointerdown' event to start dragging slides.
   * @param {PointerEvent} event - The pointerdown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;
    // Let native controls / nested interactive widgets keep their own gestures
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    // Only infinite slideshows reserve horizontal touch gestures for this drag (see the
    // touch-action rule in slideshow-styles). Everywhere else the browser pans the scroller
    // itself, and driving it from here as well makes the two fight over the same gesture.
    if (event.pointerType !== 'mouse' && !this.infinite) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    // Mouse: prevent native image drag. Touch: wait until movement so taps still click slide links.
    if (event.pointerType === 'mouse') {
      event.preventDefault();
    }
    // Store initial position but don't start handling yet.
    // Use clientX/Y — Firefox PointerEvent may not expose event.x / event.y.
    const { axis } = this.#scroll;
    const pointerCoord = (pointerEvent) => (axis === 'x' ? pointerEvent.clientX : pointerEvent.clientY);
    const startPosition = pointerCoord(event);
    const { scroller } = this.refs;

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;
    /** Accumulated pointer delta that could not move the scroller (at start/end). */
    let blockedDelta = 0;
    /** Sub-pixel pointer movement carried over to the next move event. */
    let pendingDelta = 0;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = pointerCoord(event);
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        try {
          scroller.setPointerCapture(event.pointerId);
        } catch {
          // Pointer may already be captured from pointerdown.
        }
        event.preventDefault();

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        // Nested non-infinite slideshows yield to the parent at their ends.
        // Infinite slideshows must keep the gesture so they can wrap on release.
        const cannotMoveInDirection =
          !this.infinite && ((movingRight && this.atStart) || (movingLeft && this.atEnd));

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      // The scroller discards movement below a pixel, which on high refresh rate screens is most
      // of a slow drag. Carry the remainder over so the slides keep up with the finger.
      pendingDelta += delta;
      const wholeDelta = Math.trunc(pendingDelta);
      if (!wholeDelta) return;
      pendingDelta -= wholeDelta;

      const scrollEdge = axis === 'x' ? 'scrollLeft' : 'scrollTop';
      const before = scroller?.[scrollEdge] ?? 0;
      this.#scroll.by(wholeDelta, { instant: true });
      const after = scroller?.[scrollEdge] ?? before;
      // At the first/last slide the scroller cannot move further; keep the
      // blocked delta so infinite carousels can still wrap on pointer up.
      if (Math.abs(after - before) < 0.5) {
        blockedDelta += wholeDelta;
      } else {
        blockedDelta = 0;
      }
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();
      const signedTravel = startPosition - previous;

      // `velocity` comes from the last pointermove alone, so the few pixels of drift
      // that a mouse release adds can point the opposite way to the gesture itself.
      // Stepping on that sent the carousel off in the direction the hand never went,
      // most visibly at the first slide, where there is no travel to explain it.
      const flicked = Math.abs(velocity) >= 10 && distanceTravelled >= 10;
      const flickFollowsGesture = signedTravel === 0 || direction === Math.sign(signedTravel);
      const modifier = current !== next || !flicked || !flickFollowsGesture ? 0 : direction;
      const blockedWrapThreshold = 40;
      const intendedIndex = next + modifier;
      const wrapForward =
        this.infinite &&
        next >= slides.length - 1 &&
        (intendedIndex > slides.length - 1 || blockedDelta > blockedWrapThreshold);
      const wrapBackward =
        this.infinite && next <= 0 && (intendedIndex < 0 || blockedDelta < -blockedWrapThreshold);
      // Firefox often refuses to move scrollLeft at the first/last slide (overscroll /
      // history swipe), which leaves a wrapping carousel one drag short of the loop.
      // Pointer travel decides the slide there instead of the scroller.
      //
      // A carousel that cannot wrap is left out of this. Its index also stops changing
      // once the scroller is at the end, so any short drag there looked "stuck" and
      // took a whole page of slides in the direction of travel — backwards for a drag
      // that ended moving right, which is what made the carousel swing away from the
      // end. Without a wrap to reach, the snap below is the right answer.
      const stuckAtEdge =
        this.infinite &&
        current === next &&
        Math.abs(signedTravel) >= blockedWrapThreshold &&
        distanceTravelled >= blockedWrapThreshold;

      this.removeAttribute('dragging');
      try {
        scroller.releasePointerCapture(event.pointerId);
      } catch {
        // Pointer may already have been released.
      }

      // Infinite loop: drag past the last/first slide should wrap, not clamp.
      // Also advance when the scroller was stuck at an edge for the whole drag.
      if (wrapForward || wrapBackward || stuckAtEdge) {
        this.#scroll.snap = true;
        if (wrapForward || (stuckAtEdge && signedTravel > 0)) {
          await this.next(undefined, { animate: true });
        } else {
          await this.previous(undefined, { animate: true });
        }
        if (this.#dragging) return;
        this.resume();
        return;
      }

      const newIndex = clamp(intendedIndex, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      const absoluteThumbnailIndex = this.refs.slides?.indexOf(newSlide) ?? newIndex;

      this.#centerSelectedThumbnail(absoluteThumbnailIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('dragstart', preventDefault, { capture: true, signal });
    document.addEventListener('pointermove', onPointerMove, { signal, passive: false });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * A later pointerdown (different pointer) ends the gesture — e.g. zoom dialog.
     * Ignore the same pointerId so the initiating pointerdown doesn't abort itself
     * when it bubbles to document after we switched from mousedown → pointerdown.
     */
    const startPointerId = event.pointerId;
    document.addEventListener(
      'pointerdown',
      (nextEvent) => {
        if (nextEvent.pointerId === startPointerId) return;
        onPointerUp(nextEvent);
      },
      { signal }
    );
    document.addEventListener('pointercancel', onPointerUp, { signal });
    // Do not listen for pointercapturelost: Firefox fires it at the first/last
    // slide (overscroll) and that would abort the drag before wrap/next can run.
  };

  #handlePointerEnter = (event) => {
    // Touch fires pointerenter at touchdown, same instant as the tap itself. Setting
    // `actioned` here flips neighbouring slides' content-visibility mid-gesture, which
    // is enough for iOS/Chrome to cancel the tap's synthetic click. Only a real mouse
    // hover should arm this — mirrors the guard in product-card.js's hover handler.
    if (event.pointerType && event.pointerType !== 'mouse') return;
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    // PDP mobile: never auto-scroll the thumbnails strip (video thumb tap
    // was recentering/swiping the list).
    if (
      this.closest('media-gallery') &&
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 749px)').matches
    ) {
      return;
    }

    const { thumbnails, thumbnailsContainer, slideshowControls } = this.refs;
    if (!thumbnails?.length) return;

    const activeSlide = this.slides?.[this.current];
    const activeSlideId = activeSlide?.getAttribute('slide-id');
    const selectedThumbnail =
      (activeSlideId
        ? thumbnails.find(
            (thumb) => thumb instanceof HTMLElement && thumb.getAttribute('data-slide-id') == activeSlideId
          )
        : undefined) ?? thumbnails[index];

    if (!(selectedThumbnail instanceof HTMLElement)) return;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    // Skip recentering when the thumb is already fully visible.
    const thumbRect = selectedThumbnail.getBoundingClientRect();
    const containerRect = thumbnailsContainer.getBoundingClientRect();
    const alreadyVisible =
      thumbRect.left >= containerRect.left - 1 &&
      thumbRect.right <= containerRect.right + 1 &&
      thumbRect.top >= containerRect.top - 1 &&
      thumbRect.bottom <= containerRect.bottom + 1;
    if (alreadyVisible) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }

  /**
   * Ensures scroll controls exist when filtered slides become navigable.
   * Shade galleries often start with one visible slide, so the slideshow may
   * have skipped full setup until more slides are revealed.
   */
  ensureControls() {
    const slideCount = this.slides?.length || 0;
    if (slideCount <= 1) return;

    if (this.#scroll) {
      this.#setupIntersectionObserver();
      return;
    }

    this.#setupSlideshow();
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
